import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Play, Plus, ArrowRight } from "lucide-react";
import { Nav, Footer } from "./Nav";
import { Card } from "./Card";
import { useShop } from "./useShop";
import { MAAT_LAWS, GEOMETRY } from "../data/mock";
import { Seo } from "./Seo";

export function Musbat() {
  const { catalog, wishedIds, toggleWish, buy, partner, openItem } = useShop();
  const [open, setOpen] = useState(null);
  const has = (id) => catalog.find((c) => c.id === id);
  const byIds = (ids) => ids.map(has).filter(Boolean);
  const cp = { wishedIds, onWish: toggleWish, onBuy: buy, onPartner: partner, onOpen: openItem };

  const Row = ({ title, tagline, color = "green", ids }) => {
    const items = byIds(ids);
    if (!items.length) return null;
    return (
      <section className="sec"><div className="wrap">
        <div className={`row-head ${color}`}><span className="bar" /><h2>{title}</h2><span className="tagline">{tagline}</span></div>
        <div className="card-row">{items.map((it) => <Card key={it.id} item={it} wished={wishedIds.includes(it.id)} {...cp} />)}</div>
      </div></section>
    );
  };

  return (
    <>
      <Nav />
      <Seo title="Musbat-u — Where It All Begins" description="Musbat-u: the foundational tradition at the heart of the Wu'sabat path at Ely Wellbeing." />
      <section className="hero" style={{ background: "radial-gradient(1000px 500px at 70% -10%,rgba(139,26,26,.6),transparent 55%),linear-gradient(180deg,#241110,#141414)" }}>
        <div className="wrap hero-inner">
          <span className="badge-gold">MUS BAT-U</span>
          <h1 style={{ marginTop: "1rem" }}>Walk the Path<br />of Maat.</h1>
          <p className="lead">Truth, balance, order and reciprocity — the ancient Egyptian Wu'sabat path applied to everyday living.</p>
          <div className="as-above">“As above, so below.”</div>
          <p className="lead" data-testid="wusabat-definition" style={{ marginTop: "1.25rem", fontSize: "1rem", color: "#e7d9c9", maxWidth: "60ch" }}>
            <strong>What is Wu'sabat?</strong> As the tradition presents it, Wu'sabat is an ancient culture — held to
            reach back to the first people of the earth — whose Master Teacher is Dr. Malachi Z. Kobina York. Its
            followers are known as Sabeans, Nubians and Mus'bat‑u, and the discipline they practise is called
            <em> Nuwaupu</em> — defined in the tradition's own texts as “the science of sound right reasoning… right
            knowledge, right wisdom and a right overstanding.”
          </p>
          <p className="muted" data-testid="wusabat-nlp-note" style={{ marginTop: ".75rem", fontSize: ".85rem", maxWidth: "60ch" }}>
            Enrolled NLP Practitioner and Master Practitioner students receive a companion manual mapping each NLP
            technique to this Wu'sabat perspective, with sourced quotations.
          </p>
          <div className="actions">
            <a className="btn btn-primary" href="#laws"><Play size={16} /> Begin</a>
            <Link className="btn btn-gold-outline" to="/digital"><Plus size={16} /> My List</Link>
          </div>
        </div>
      </section>

      {/* 11 Laws */}
      <section className="sec pad" id="laws"><div className="wrap">
        <div className="row-head green"><span className="bar" /><h2>The 11 Laws of Maat</h2><span className="tagline">An ancient framework for living well.</span></div>
        <div className="laws-grid">
          {MAAT_LAWS.map((l, i) => (
            <div key={l.n} className="law" onClick={() => setOpen(open === i ? null : i)}>
              <div className="ln">LAW {l.n}</div><h3>{l.name}</h3><div className="theme">{l.theme}</div>
              {open === i && <div className="expand"><p className="muted">{l.connection}</p></div>}
            </div>
          ))}
        </div>
      </div></section>

      <Row title="Maat in Daily Life" tagline="Ancient wisdom for modern living." color="green" ids={["maat_masterclass", "maat_journal", "maat_journaling_circle", "wusabat_foundations"]} />
      <Row title="The Wu'sabat Herbal Codex" tagline="Plant allies of the ancients, for modern healing." color="green" ids={["materia_medica", "wusabat_herbal_codex", "herb_chamomile", "herb_turmeric"]} />
      <Row title="Know Thyself" tagline="Self-discovery starts here." color="gold" ids={["ely_book", "shadow_work", "maat_journal"]} />

      {/* Sacred Geometry */}
      <section className="sec pad"><div className="wrap">
        <div className="row-head gold"><span className="bar" /><h2>Sacred Geometry</h2><span className="tagline">As above, so below.</span></div>
        <div className="geo-grid">
          {GEOMETRY.map((g) => <div className="geo" key={g.title}><div className="sym">{g.symbol}</div><h3>{g.title}</h3><p className="muted">{g.body}</p></div>)}
        </div>
      </div></section>

      <Row title="The Wu'sabat Path — Full Journey" tagline="Deep study. True mastery. Walk the path." color="red" ids={["wusabat_foundations", "golden_proportion", "somatic", "ely_plus"]} />

      <section className="sec pad"><div className="wrap">
        <div className="band gold">
          <span className="eyebrow" style={{ color: "#241c05" }}>THE MA'AT PROMISE</span>
          <h2 style={{ color: "#241c05", marginTop: ".4rem" }}>Truth, balance & accountability</h2>
          <p style={{ color: "#3a2f10", maxWidth: 560, margin: ".4rem auto 1.2rem", fontWeight: 700 }}>ElyWellbeing distinguishes evidence, traditional knowledge, spiritual teaching and historical discussion — so you always know the nature of what you're learning.</p>
          <Link className="btn btn-primary" to="/hub">Open my Maat journal <ArrowRight size={16} /></Link>
        </div>
      </div></section>
      <Footer />
    </>
  );
}
